import { faCheckCircle, faPencilAlt, faPlusCircle, faTimesCircle, faTrashAlt } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from "react"
import { ReactSortable } from "react-sortablejs"
import { toast } from "react-toastify"
import { StoreConnector, StoreProps } from "../../redux/Actions"

const API2 = process.env.REACT_APP_API2

interface TrainerSpecialityListItem {
  id: number
  name: string
  description: string
  cover?: string
  background?: string
}

function TrainerSpeciality(props: StoreProps) {
  const { token } = props

  const [specialityList, setSpecialityList] = useState<Array<TrainerSpecialityListItem>>([])
  const [focusedSpeciality, setFocusedSpeciality] = useState<number>()
  const [name, setName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [cover, setCover] = useState<File>()
  const [background, setBackground] = useState<File>()
  const [previewCover, setPreviewCover] = useState<string>()
  const [previewBackground, setPreviewBackground] = useState<string>()
  const [showEditSpecialityModal, setEditSpecialityModalVisibility] = useState<boolean>(false)
  const [updateTimestamp, setUpdateTimestamp] = useState<number>(Date.now())

  const [findQuery, setFindQuery] = useState<string>('')

  const specialithForm = (id?: number) => {
    const foundData = specialityList.find(data => data.id === id)
    
    setFocusedSpeciality(id)
    setName(foundData?.name ?? '')
    setDescription(foundData?.description ?? '')
    setPreviewCover(foundData?.cover)
    setPreviewBackground(foundData?.background)
    setCover(undefined)
    setBackground(undefined)
    setEditSpecialityModalVisibility(true)
  }

  const uploadImagehandler = (file: FileList|null, preview: Dispatch<SetStateAction<string|undefined>>, storeTo: Dispatch<SetStateAction<File|undefined>>) => {
    if (file === null) {
      return
    }

    if (file.length < 1) {
      return
    }

    if (file[0].size > 1000000) {
      toast('Ukuran file maksimal adalah 1MB', {type: 'error'})
      return
    }

    const reader = new FileReader()

    reader.onload = (ev: ProgressEvent<FileReader>) => {
      const result = ev.target?.result

      if (!result) {
        return
      }

      preview(result.toString())
    }
    
    reader.readAsDataURL(file[0])
    storeTo(file[0])
  }

  const removeSpeciality = (id: number) => {
    if (!window.confirm('Anda yakin menghapus data ini?') || !token) {
      return
    }

    const fetchInitOpt: RequestInit = {
      method: 'DELETE',
      headers: {
        Authorization: token
      }
    }

    fetch(`${API2}/administration/trainer-speciality/${id}`, fetchInitOpt)
      .then(response => response.json())
      .then(response => {
        if (response.error) {
          toast('Gagal menyimpan data!', {type: 'error'})
          return
        }

        toast('Data berhasil dihapus!', {type: 'success'})
        setUpdateTimestamp(Date.now())
      })
      .catch(() => toast('Terjadi kegagaln jaringan!', {type: 'error'}))
  }

  const submitSpeciality = () => {
    if (!token) {
      return
    }

    const form = cover || background ? new FormData() : new URLSearchParams()

    form.append('id', String(focusedSpeciality))
    form.append('name', name)
    form.append('description', description)
    
    if (background) {
      form.append('background', background)
    }

    if (cover) {
      form.append('cover', cover)
    }

    const fetchInitOpt: RequestInit = {
      method: 'POST',
      headers: {
        Authorization: token
      },
      body: form
    }

    fetch(`${API2}/administration/trainer-speciality`, fetchInitOpt)
      .then(response => response.json())
      .then(response => {
        if (response.error) {
          toast('Gagal menyimpan data!', {type: 'error'})
          return
        }

        toast('Berhasil menyimpan data!', {type: 'success'})
        setUpdateTimestamp(Date.now())
        setEditSpecialityModalVisibility(false)
      })
      .catch(() => toast('Gagal menyimpan data!', {type: 'error'}))
  }

  const sortHandler = () => {
    if (!token) {
      return
    }

    const form = new URLSearchParams()

    specialityList.map((speciality, index) => form.append(`sorted_speciality[${index}]`, String(speciality.id)))

    const fetchInitOpt: RequestInit = {
      method: 'POST',
      headers: {
        Authorization: token
      },
      body: form
    }

    fetch(`${API2}/administration/sort-trainer-speciality`, fetchInitOpt)
      .then(response => response.json())
      .then(response => {
        if (response.message === 'ERROR') {
          toast('Gagal memperbarui urutan!', {type: 'error'})
          return
        }

        setUpdateTimestamp(Date.now())
      })
      .catch(() => toast('Terjadi kegagalan jaringan!', {type: 'error'}))
  }

  useEffect(() => {
    if (!token) {
      return
    }

    const fetchInitOpt: RequestInit = {
      headers: {
        Authorization: token
      }
    }

    fetch(`${API2}/administration/trainer-speciality`, fetchInitOpt)
      .then(response => response.json())
      .then(response => {
        if (response.message === 'ERROR') {
          toast('Gagal mengambil data!', {type: 'error'})
          return
        }

        setSpecialityList(response)
      })
      .catch(() => toast('Kegagalan jaringan!', {type: 'error'}))
  }, [token, updateTimestamp])

  return (
    <Fragment>
      <div className="mt-3">
        <div className="columns is-centered">
          <div className="column is-6">
            <div className="box">
              <div className="level">
                <div className="level-left">
                  <div className="level-item">
                    <h4 className="title is-size-4">Trainer Speciality List</h4>
                  </div>
                </div>
                <div className="level-right">
                  <div className="level-item">
                    <input type="text" className="input is-small mr-1" placeholder="Cari" value={findQuery} onChange={ev => setFindQuery(ev.target.value)} />
                    <button className="button is-small is-primary" onClick={() => specialithForm()}>
                      <div className="icon is-small is-left">
                        <FontAwesomeIcon icon={faPlusCircle}/>
                      </div>
                      <span>Tambah</span>
                    </button>
                  </div>
                </div>
              </div>
              <ReactSortable list={specialityList} setList={setSpecialityList} onEnd={sortHandler}>
                {specialityList.map(speciality => (
                  <div className="box" key={speciality.id} onDrag={ev => ev.currentTarget.className = 'box has-background-warning'} style={{cursor: 'move'}}>
                    <div className="columns">
                      <div className="column is-narrow">
                        <img src={speciality.cover} alt="" width={150} height={150} style={{objectFit: 'contain'}} />
                      </div>
                      <div className="column">
                        <h6 className="title is-size-6">{speciality.name}</h6>
                        <p className="my-3">{speciality.description}</p>
                        <button className="button is-small is-primary" onClick={() => specialithForm(speciality.id)}>
                          <div className="icon is-small is-left">
                            <FontAwesomeIcon icon={faPencilAlt}/>
                          </div>
                          <span>Ubah</span>
                        </button>
                        <button className="button is-danger is-small ml-1" onClick={() => removeSpeciality(speciality.id)}>
                          <div className="icon is-small is-left">
                            <FontAwesomeIcon icon={faTrashAlt}/>
                          </div>
                          <span>Hapus</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </ReactSortable>
            </div>
          </div>
        </div>
      </div>
      {showEditSpecialityModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setEditSpecialityModalVisibility(false)}></div>
          <div className="modal-content">
            <div className="box">
              <div className="level">
                <div className="level-left">
                  <div className="level-item">
                    <h4 className="title is-size-4">Form Speciality</h4>
                  </div>
                </div>
                <div className="level-right">
                  <div className="level-item">
                    <button className="button is-small is-primary" onClick={submitSpeciality}>
                      <div className="icon is-small is-left">
                        <FontAwesomeIcon icon={faCheckCircle}/>
                      </div>
                      <span>Simpan</span>
                    </button>
                    <button className="button is-small is-default ml-1" onClick={() => setEditSpecialityModalVisibility(false)}>
                      <div className="icon is-small is-left">
                        <FontAwesomeIcon icon={faTimesCircle}/>
                      </div>
                      <span>Batal</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="field">
                <label htmlFor="">Nama</label>
                <div className="control">
                  <input type="text" className="input" value={name} onChange={ev => setName(ev.target.value)} />
                </div>
              </div>
              <div className="field">
                <label htmlFor="">Description</label>
                <div className="control">
                  <textarea className="textarea" value={description} onChange={ev => setDescription(ev.target.value)} />
                </div>
              </div>
              <div className="columns mt-0">
                <div className="column">
                  <div className="field">
                    <label htmlFor="">Cover</label>
                    <div className="control">
                      <input
                        type="file"
                        className="input"
                        onChange={ev => uploadImagehandler(ev.target.files, setPreviewCover, setCover)}
                        accept="image/*"
                      />
                    </div>
                    {previewCover !== undefined && (
                      <img src={previewCover} alt="" className="image mt-1" />
                    )}
                  </div>
                </div>
                <div className="column">
                  <div className="field">
                    <label htmlFor="">Background</label>
                    <div className="control">
                      <input
                        type="file"
                        className="input"
                        onChange={ev => uploadImagehandler(ev.target.files, setPreviewBackground, setBackground)}
                        accept="image/*"
                      />
                    </div>
                    {previewBackground !== undefined && (
                      <img src={previewBackground} alt="" className="image mt-1" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  )
}

export default StoreConnector(TrainerSpeciality)